import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Link } from "react-router-dom";

import { useForm } from "react-hook-form";

import usePartBins, { usePartBinParts } from "../../data/usePartBins";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../notifications/useNotifications";
import Modal from "../../layouts/Modal";
import { IPartBin } from "../../models/PartBin";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { TextInput } from "../shared/Inputs/TextInput";
import LoadingWheel from "../shared/LoadingWheel";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import printHtml from "../../utils/printHtml";

type FormValues = {
  name: string;
};

interface PartBinsModalProps {
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  partBin?: IPartBin;
}

export default function PartBinsModal({ open, setOpen, partBin }: PartBinsModalProps) {
  const { t } = useTranslation("stock");
  const { saveHandlers } = useDefaultCRUDHandlers(t("Part Bin"));
  const { deleteHandlers } = useDefaultCRUDHandlers(t("Part Bin"));
  const { addNotification } = useNotification();
  const { generatePartBinLabel } = useLabelGenerator();
  const { update, create, remove } = usePartBins();
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [partsOpen, setPartsOpen] = useState<boolean>(false);
  const { register, handleSubmit, reset } = useForm<FormValues>();
  const binParts = usePartBinParts(partBin?.id, partsOpen);

  // The API refuses a duplicate bin name and refuses to delete a bin that still holds parts. Both
  // come back as a message the operator needs to read, so show it rather than the generic toast.
  const showApiError = (error: unknown, fallback: string) =>
    addNotification({
      variant: "error",
      primaryText: (error as Error)?.message ?? fallback,
    });

  const onSubmit = (data: FormValues) => {
    const updatedPartBin = {
      ...partBin,
      ...data
    } as IPartBin;

    if (partBin !== undefined) {
      update.mutate(updatedPartBin, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: (error) => showApiError(error, t("Failed to save part bin"))
      });
    } else {
      create.mutate(updatedPartBin, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: (error) => showApiError(error, t("Failed to save part bin"))
      });
    }
  }

  const handleDelete = () => {
    remove.mutate(partBin?.id!, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setOpen(false);
        setDeleteOpen(false);
        reset({});
      },
      onError: (error) => {
        setDeleteOpen(false);
        showApiError(error, t("Failed to delete part bin"));
      }
    });
  };

  const handlePrint = async () => {
    const html = await generatePartBinLabel(partBin!);
    printHtml(html);
  };

  useEffect(() => {
    if (partBin !== undefined) {
      reset({
        name: partBin.name
      });
    } else {
      reset({})
    }
  }, [partBin, open, reset]);

  return (
    <>
      <ConfirmModal open={deleteOpen} setOpen={setDeleteOpen}
        title={t("Delete Bin")} message={t("Are you sure you want to delete this bin?\n This action cannot be undone.")}
        confirmButtonText={t("Delete")} onConfirm={handleDelete} isLoading={false}
      />
      <Modal width="max-w-2xl" open={open} setOpen={setOpen} >
        {/* The modal draws its own close X in the top right, so keep this row clear of it. */}
        <h1 className="text-xl">{partBin ? t("Edit a Bin") : t("Add a Bin")}</h1>
        <div className="mt-4">
          <form className="space-y-4 border-t border-gray-200" onSubmit={handleSubmit(onSubmit)} >
            <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-1">

              <TextInput label={t("Name")} registerName="name" register={register} />

              {partBin !== undefined && (
                <p className="text-sm text-gray-500">
                  {(partBin.partCount ?? 0) === 1
                    ? t("{{count}} part in this bin", { count: partBin.partCount ?? 0 })
                    : t("{{count}} parts in this bin", { count: partBin.partCount ?? 0 })}
                </p>
              )}

              {partBin !== undefined && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <ButtonInput label={t("View Parts")} isSubmit={false} onClick={() => setPartsOpen(true)}
                    classes={"!bg-white !text-gray-700 !border-gray-300 hover:!bg-gray-50"} />
                  <ButtonInput label={t("Print Label")} isSubmit={false} onClick={() => handlePrint()}
                    classes={"!bg-white !text-gray-700 !border-gray-300 hover:!bg-gray-50"} />
                </div>
              )}

              <ButtonInput label={partBin ? t("Save Changes") : t("Create")} isSubmit={true} onClick={undefined} classes={undefined} />
              {partBin !== undefined && (
                <ButtonInput label={t("Delete")} isSubmit={false} onClick={() => (setDeleteOpen(true))} classes={"!bg-red-500 hover:!bg-red-600"} />
              )}
            </div>
          </form>
        </div>
      </Modal>

      <Modal width="max-w-lg" open={partsOpen} setOpen={setPartsOpen}>
        <div className="space-y-3">
          <div>
            <h1 className="text-lg font-semibold text-gray-800">{t("Parts in this bin")}</h1>
            <p className="text-sm text-gray-500">{partBin?.name}</p>
          </div>
          <hr />
          {binParts.isLoading ? (
            <LoadingWheel />
          ) : (binParts.data?.data ?? []).length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">{t("This bin is empty.")}</p>
          ) : (
            <ul className="max-h-96 divide-y divide-gray-100 overflow-y-auto">
              {(binParts.data?.data ?? []).map((part) => (
                <li key={part.id}>
                  <Link
                    to={`/stock/${part.id}`}
                    className="flex items-center justify-between gap-3 py-2 hover:bg-gray-50"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-800">
                      {part.title ?? t("Part")}
                    </span>
                    {part.tagNumber && (
                      <span className="shrink-0 text-xs text-gray-500">{part.tagNumber}</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Modal>

    </>
  );
}
